'use client';

import { Sizes, ThemeColorValueImportance, ThemeColorValueTypes } from '@actr/style/src/specs/theme-v2';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { Accordion, AccordionItem } from '@actr/ui/src/components/accordion';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { WhereToFlyItem } from '@/services/models/avia/where_to_fly';

import * as Styled from './style.css';

export type WhereToFlyProps = {
   title: string;
   subtitle: string;
   countries: WhereToFlyItem[];
   cities: WhereToFlyItem[];
   directions: WhereToFlyItem[];
   allCitiesLink?: string;
}

enum FlightVariants {
  COUNTRIES = 'Популярные страны',
  CITIES = 'Популярные города',
  ROUTES = 'Популярные направления'
}

const WhereToFly: React.FC<WhereToFlyProps> = ({ title, subtitle, countries, cities, directions, allCitiesLink = '' }) => {
  function renderLinks(items: WhereToFlyItem[], value?: string) {
    let hubPageHref = '';

    switch (value) {
      case FlightVariants.COUNTRIES: hubPageHref = '/countries'; break;
      case FlightVariants.CITIES: hubPageHref = `/${allCitiesLink}#from_country_cities`; break;
      case FlightVariants.ROUTES: hubPageHref = '/routes'; break;
      default: break;
    }

    return (
      <>
        {items.map((item, i) => (
          <a
            className={Styled.Link}
            href={`/${item.url}`}
            key={i}>
            <meta itemProp="position" content={(i + 1).toString()} />
            <Text
              colorType={ThemeColorValueTypes.INFO}
              font="paragraph.s">
              {item.name}
            </Text>
          </a>
        ))}
        {value === FlightVariants.CITIES && !allCitiesLink ? null : (
          <a className={Styled.ButtonLink} href={hubPageHref}>
            <Text colorType={ThemeColorValueTypes.INFO} font="paragraph.s.semi">
              Все
              {' '}
              {value?.toLowerCase()}
            </Text>
          </a>
        )}
      </>
    );
  }

  function renderDesktopBlock(items: WhereToFlyItem[], value: FlightVariants) {
    if (items?.length) {
      return (
        <div className={Styled.DesktopBlockWrapper}>
          <Text className={Styled.Title} font="title.s">{value}</Text>
          {renderLinks(items, value)}
        </div>
      );
    }
    return null;
  }

  const mobileAccordionItems: AccordionItem[] = [
    { items: countries, value: FlightVariants.COUNTRIES },
    { items: cities, value: FlightVariants.CITIES },
    { items: directions, value: FlightVariants.ROUTES },
  ].filter(({ items }) => items?.length).map(({ items, value }) => ({
    title: value,
    content: (
      <div className={Styled.MobileLinksWrapper}>
        {renderLinks(items, value)}
      </div>
    ),
  }));

  return (
    <ContentBlock className={Styled.ContentBlock} id="where_to_fly" tagProps={{ itemScope: true, itemType: 'https://schema.org/ItemList' }}>
      <meta itemProp="numberOfItems" content={countries.concat(cities, directions).length.toString()} />
      <Text tagName="h3" font="title.x2l" tagProps={{ itemProp: 'name' }}>{title}</Text>
      <Space size={Sizes.X3S} />
      <Text color={ThemeColorValueImportance.LIGHTER} font="paragraph.m">{subtitle}</Text>
      <div className={Styled.MainInfoWrapper} itemScope itemProp="itemListElement" itemType="https://schema.org/ListItem">
        {renderDesktopBlock(countries, FlightVariants.COUNTRIES)}
        {renderDesktopBlock(cities, FlightVariants.CITIES)}
        {renderDesktopBlock(directions, FlightVariants.ROUTES)}
        {mobileAccordionItems.length > 0 && (
          <div className={Styled.MobileBlockWrapper}>
            <Accordion items={mobileAccordionItems} defaultOpenIndex={0} />
          </div>
        )}
      </div>
    </ContentBlock>
  );
};

export default observer(WhereToFly);
